import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Observable, Subject, throwError } from 'rxjs';
import { AuthService } from '@http/auth.service';
import { environment } from '@environment';
import { ErrorModel } from '@models/error.model';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { AuthUserModel } from '@models/auth-user.model';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { captureException, ServerResponseError } from '@core/sentry-lazy';
import { SKIP_AUTH } from '@core/http-context';

@Injectable()
export class HttpInterceptorService implements HttpInterceptor {

  refreshTokenInProgress = false;
  tokenRefreshedSource = new Subject<void>();
  tokenRefreshed$ = this.tokenRefreshedSource.asObservable();

  constructor(private authService: AuthService,
              private http: HttpClient,
              private router: Router,
              private toastr: ToastrService,
              private translate: TranslateService,
              private localize: LocalizeRouterService) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<any> {

    // Pass login request without modification
    if (req.url.indexOf('/login') !== -1) {
      return next.handle(req);
    }

    return next.handle(this.appendToken(req))
      .pipe(
        catchError(error => {
          if (error.status === 401) {
            // Retry community request without token and log out
            if (error.url.indexOf('/community') !== -1) {
              this.authService.logout();
              return next.handle(req);
            }
            if (error.url.indexOf('/auth/refresh') !== -1) {
              this.refreshTokenInProgress = false;
              this.toastr.warning(this.translate.instant('GLOBAL.SESSION-EXPIRED'));
              this.router.navigate([this.localize.translateRoute('/member/login')]);
              return Observable.create('');
            }
            return this.refreshToken().pipe(switchMap(() => {
              return next.handle(this.appendToken(req)).pipe(catchError(this.handleError));
            }));
          }
          return this.handleError(error);
        })
      );

  }

  private appendToken(req: HttpRequest<any>): HttpRequest<any> {
    // Requests to hosts that are not ours opt out — see SKIP_AUTH. Without this the
    // member's bearer token would be sent to every origin the app talks to.
    if (req.context.get(SKIP_AUTH)) {
      return req;
    }
    if (this.authService.isLoggedIn() && this.authService.authenticatedUser?.access) {
      return req.clone({ setHeaders: { Authorization: 'Bearer ' + this.authService.authenticatedUser.access.token }});
    } else {
      return req;
    }
  }

  refreshToken(): Observable<unknown> {
    if (this.refreshTokenInProgress) {
      return new Observable<void>(observer => {
        this.tokenRefreshed$.subscribe(() => {
          observer.next();
          observer.complete();
        });
      });
    } else {
      this.refreshTokenInProgress = true;
      const url = environment.memberApiUrl + 'auth/refresh';
      const body = { token: this.authService.authenticatedUser!.refresh.token };
      return this.http.post<AuthUserModel>(url, body)
        .pipe(
          map(refreshResponse => {
            this.authService.setToken(refreshResponse);
            return refreshResponse;
          }),
          tap(() => {
            this.refreshTokenInProgress = false;
            this.tokenRefreshedSource.next();
          })
        );
    }
  }

  private handleError(error: HttpErrorResponse): Observable<string> {

    // Capture sentry event: server failures and 4xx without a dewabit error
    // document. A 4xx with message and code is an expected answer the user
    // sees (licence, subscription, validation); status 0 is a dropped
    // connection on the visitor's side.
    const body = error.error;
    const hasDocument = !!(body && typeof body === 'object' && typeof (body as ErrorModel).message === 'string' && (body as ErrorModel).message);
    if (error.status >= 500 || (error.status > 0 && !hasDocument)) {
      const reported = new ServerResponseError(error.status, error.url, (body as ErrorModel)?.code, body);
      captureException(reported, reported.captureContext);
    }

    if (error.status === 0) {
      // A client-side or network error occurred. Handle it accordingly.
      return throwError(error.message);
    } else {
      // The backend returned an unsuccessful response code.
      if (error.error?.message) {
        const errModel = error.error as ErrorModel;
        return throwError(errModel.message);
      } else {
        return throwError(error.message);
      }
    }
  }

}
